import { createContext, useContext } from 'react';

import type { CallPrefill } from '../calls/CallPop';
import type { OrderDetail } from '../orders/api';

/**
 * What the three dispatch views share.
 *
 * All of it used to live in `Console.tsx` because there was one screen. With three, the
 * state has to outlive the view: a dispatcher who opens an order on the desk, presses `2`
 * to see where the driver is, and comes back must find the same order open — and the caller
 * whose details are half-typed into the form must still be there.
 *
 * The context and its hook live apart from the provider component for the same reason
 * `authContext.ts` does: `react/only-export-components` warns on a module that exports both,
 * and Fast Refresh cannot reload one that does.
 */
export interface DispatchState {
  /** The order whose detail is open, on whichever view is showing. */
  selectedOrder: OrderDetail | null;
  setSelectedOrder: (order: OrderDetail | null) => void;

  /** The caller whose details the order form is prefilled from, when a call was taken. */
  fromCall: CallPrefill | null;
  takeCall: (prefill: CallPrefill | null) => void;

  /**
   * The order taken on the call that is still up.
   *
   * Both halves are needed: the call is what gets parked, and the order is what the caller
   * will be waiting for. Cleared when the pop changes, so a dispatcher cannot park the next
   * caller against the previous caller's order.
   */
  orderOnCall: { callId: string; orderId: string } | null;

  /** An order was just taken: remembered for the call, and opened, so the queue shows it selected. */
  onCreated: (order: OrderDetail) => void;

  /** The reference of the last order taken, which stays on screen until the next one. */
  lastTaken: string | null;

  /**
   * The numbers currently on screen as a pop.
   *
   * The telephone card reads this to decide whether to offer Answer at all. Taking a call
   * answers it, so a caller who is already on screen needs one button and not two — but a
   * call that rings with no pop, an internal extension or a caller the lookup failed on,
   * still has to be answerable from somewhere.
   */
  popped: string[];
  setPopped: (numbers: string[]) => void;

  /**
   * A car somebody asked to see on the map — the short number in the queue was pressed.
   *
   * A request rather than a selection: the map opens that car's card, moves to it and
   * clears this, so pressing the same number again works, and a map that is not on the
   * screen yet (the desk's loads lazily, the timeline has none) picks it up when it is.
   */
  driverToShow: string | null;
  showDriver: (driverId: string | null) => void;
}

export const DispatchContext = createContext<DispatchState | null>(null);

export function useDispatch(): DispatchState {
  const state = useContext(DispatchContext);

  if (state === null) {
    throw new Error('A dispatch view was rendered outside the console shell.');
  }

  return state;
}
